import * as React from "react"
import { build as text } from "@modules/locales/default.js"
import { Spacer, ThemeCss } from "gatsby-interface"

export type PageType = {
  label: string
  value: number
  percentage: number
  color: number
}

export type BuildPageTypesStatsProps = {
  ssgCount?: number
  dsgCount?: number
  ssrCount?: number
}

export function BuildPageTypesStats({
  ssgCount = 0,
  dsgCount = 0,
  ssrCount = 0,
}: BuildPageTypesStatsProps) {
  if (ssgCount === 0 && dsgCount === 0 && ssrCount === 0) {
    return null
  }

  const countSum = ssgCount + dsgCount + ssrCount

  const pageTypes: PageType[] = [
    {
      label: text.labels.ssr,
      value: ssrCount,
      color: 60,
      percentage: getPercentage(ssrCount, countSum),
    },
    {
      label: text.labels.dsg,
      value: dsgCount,
      color: 40,
      percentage: getPercentage(dsgCount, countSum),
    },
    {
      label: text.labels.ssg,
      value: ssgCount,
      color: 20,
      percentage: getPercentage(ssgCount, countSum),
    },
  ]

  // checks the sum of pecentage values, because of rounding or the 'manual adjustment' in getPercentage()
  // it's possible that the sum evaluates to > 100 or < 100, we want to know if that's the case
  const percentageSum = pageTypes.reduce((acc, cur) => {
    return acc + cur.percentage
  }, 0)

  // checks if the 'manual adjustment' bumped the sum above 100
  // or rounding reduced it to below 100
  if (percentageSum > 100 || percentageSum < 100) {
    // if so it looks for the most popular type
    const highestCountType = pageTypes.reduce((acc, cur) => {
      return cur.value > acc.value ? cur : acc
    })

    const diff = 100 - percentageSum

    // and adjusts its percentage value, so all parts toghether
    // never take more or less than 100% width of the container
    if (highestCountType) {
      highestCountType.percentage = highestCountType.percentage + diff
    }
  }

  return (
    <div css={rootCss}>
      {text.labels.pageTypes}
      <Spacer size={3} />

      <div css={graphCss} aria-hidden data-testid="graph">
        {pageTypes.map(({ color, percentage, label, value }) => {
          if (value === 0) {
            return null
          }

          return (
            <span
              key={label}
              css={theme => [
                fragmentCss(theme),
                {
                  width: `${percentage}%`,
                  background: theme.colors.purple[color],
                },
              ]}
              data-testid={label}
            />
          )
        })}
      </div>

      {pageTypes.map(({ label, value, color }) => {
        return (
          <span
            key={label}
            css={theme => [
              labelCss(theme),
              {
                "&:before": {
                  background: theme.colors.purple[color],
                },
              },
            ]}
          >
            {value} {label}
          </span>
        )
      })}
    </div>
  )
}

function getPercentage(part: number, sum: number) {
  const percentage = Math.round((part / sum) * 100)

  // applies 'manual adjustment'; for cases when percentage rounding evaluates to '0' but the part's value > 0
  if (part > 0 && percentage === 0) {
    return 1
  }

  return percentage
}

/* styles */

const rootCss: ThemeCss = theme => ({
  display: `flex`,
  flexWrap: `wrap`,
})

const graphCss: ThemeCss = theme => ({
  display: `flex`,
  height: `8px`,
  overflow: `hidden`,
  borderRadius: theme.radii[5],
  margin: `${theme.space[2]} 0 `,
  width: `100%`,

  [theme.mediaQueries.desktop]: {
    margin: `0.5em ${theme.space[3]} 0 ${theme.space[5]} `,
    width: `200px`,
  },
})

const fragmentCss: ThemeCss = theme => ({})

const labelCss: ThemeCss = theme => ({
  display: `inlineBlock`,
  position: `relative`,
  margin: `0 ${theme.space[3]}`,
  paddingLeft: theme.space[4],

  "&:before": {
    display: `inline-block`,
    margin: 0,
    content: `""`,
    width: `8px`,
    height: `8px`,
    borderRadius: theme.radii[6],
    marginRight: theme.space[2],
    position: `absolute`,
    left: 0,
    top: `0.5em`,
  },
})
